import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
    ActivatedRoute,
    Router,
    RouterLink,
    RouterLinkActive,
    RouterLinkWithHref,
    RouterOutlet,
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { PushPipe } from '@ngrx/component';
import {
    ActiveFeatureFacadeService,
    ExportService,
    FiltersService,
    ITenant,
    MetaDataService,
    SystemDataService,
} from '@wdx/kamino/api-service';
import { KaminoFileUploaderService } from '@wdx/kamino/features/file-uploader';
import {
    KaminoEnvironmentSelectorComponent,
    KaminoTenantComponent,
} from '@wdx/kamino/ui';
import { HOME, ICONS } from '@wdx/kamino/utils/constants';
import { KaminoRecentRecordsService } from '@wdx/kamino/utils/services';
import { WdxAppContentModule } from '@wdx/shared/components/wdx-app-content';
import { WdxAppHeaderModule } from '@wdx/shared/components/wdx-app-header';
import { WdxAppShellModule } from '@wdx/shared/components/wdx-app-shell';
import { WdxAppSidebarModule } from '@wdx/shared/components/wdx-app-sidebar';
import { Avatar } from '@wdx/shared/components/wdx-avatar';
import {
    WdxButtonModule,
    WdxButtonSize,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxIconContainerModule } from '@wdx/shared/components/wdx-icon-container';
import { WdxIsLoadingModule } from '@wdx/shared/components/wdx-is-loading';
import { WdxNavModule } from '@wdx/shared/components/wdx-nav';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import { WdxUserMenuComponent } from '@wdx/shared/components/wdx-user-menu';
import { DOWNLOAD, WdxAppThemeService, iThemeSetting } from '@wdx/shared/utils';
import { Observable, map } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

@Component({
    imports: [
        RouterOutlet,
        RouterLinkWithHref,
        RouterLink,
        RouterLinkActive,
        WdxAppHeaderModule,
        WdxAppContentModule,
        WdxAppSidebarModule,
        WdxAppShellModule,
        WdxButtonModule,
        WdxIconModule,
        WdxIconContainerModule,
        WdxSpinnerModule,
        WdxNavModule,
        WdxUserMenuComponent,
        KaminoTenantComponent,
        PushPipe,
        WdxIsLoadingModule,
        KaminoEnvironmentSelectorComponent,
        CommonModule,
    ],
    selector: 'ops-shell',
    templateUrl: './app-shell.component.html',
    styleUrls: ['./app-shell.component.scss'],
})
export class AppShellComponent implements OnInit {
    collapsed = false;
    downloading$ = new BehaviorSubject(false);
    readonly ICONS = ICONS;
    readonly HOME = HOME;

    private systemDataService = inject(SystemDataService);
    private exportService = inject(ExportService);
    private metaDataService = inject(MetaDataService);
    private authService = inject(AuthService);
    private fileUploaderService = inject(KaminoFileUploaderService);
    private wdxAppThemeService = inject(WdxAppThemeService);
    private recentRecordsService = inject(KaminoRecentRecordsService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    public activeFeatureFacadeService = inject(ActiveFeatureFacadeService);
    public filtersService = inject(FiltersService);

    whoAmI = this.systemDataService.whoAmI$?.value;
    avatar: Avatar = {
        avatarUrl: this.whoAmI?.avatarUrl,
        text: `${this.whoAmI?.firstName} ${this.whoAmI?.lastName}`,
    };
    environment = this.whoAmI?.environment;
    tenants$ = this.systemDataService.tenants$;
    metadata$ = this.metaDataService.metadata$;

    readonly WDX_BUTTON_SIZE = WdxButtonSize;
    readonly WDX_BUTTON_STYLE = WdxButtonStyle;

    get tenant(): ITenant {
        return this.systemDataService.tenant$.value as ITenant;
    }
    get themeInfo(): { icon: string; name: string } {
        return this.wdxAppThemeService.themeSetting === iThemeSetting.Light
            ? { icon: 'moon', name: 'dark' }
            : { icon: 'sun', name: 'light' };
    }

    ngOnInit(): void {
        this.recentRecordsService.setTenant(this.tenant.code);
    }

    public onCollapsed(): void {
        this.collapsed = !this.collapsed;
    }

    public wdxOnLogout(): void {
        this.authService.logout();
    }

    public exportZip(isDownloading: boolean | undefined): void {
        if (!isDownloading) {
            this.downloading$.next(true);
            this.exportService.zip().subscribe({
                next: (data) => {
                    DOWNLOAD(
                        `export-${this.environment}-${this.tenant?.code}-${
                            this.whoAmI?.firstName
                        }-${this.whoAmI?.lastName}-${Date.now()}`,
                        data,
                    );
                    this.downloading$.next(false);
                },
                error: () => {
                    this.downloading$.next(false);
                },
            });
        }
    }

    public openImport(): void {
        this.fileUploaderService.open();
    }

    public onTenantSwitch(tenant: ITenant): void {
        const featureCode = this.activeFeatureFacadeService.featureCode;
        const currentUrl = this.router.url;
        const currentQueryParams = this.route.snapshot.queryParams;

        let navigationPath = [tenant.code, HOME];

        if (featureCode && !currentUrl.includes(HOME)) {
            navigationPath = [tenant.code, featureCode];
        }

        this.router
            .navigate(navigationPath, { queryParams: currentQueryParams })
            .then(() => {
                window.location.reload();
            });
    }

    public switchTheme(): void {
        if (this.wdxAppThemeService.themeSetting === iThemeSetting.Light) {
            this.wdxAppThemeService.setToDark();
        } else {
            this.wdxAppThemeService.setToLight();
        }
    }
    public filterQueryParamsForFeature(
        featureCode: string,
    ): Observable<Record<string, string> | null> {
        return this.filtersService.getFiltersForFeature(featureCode).pipe(
            map(({ isDisable, isLocal, search, selectedFields }) => {
                const params: Record<string, string> = {
                    ...(isDisable ? { isDisable: 'true' } : {}),
                    ...(isLocal ? { isLocal: 'true' } : {}),
                    ...(search ? { search } : {}),
                    ...(selectedFields ?? {}),
                };

                return Object.keys(params).length > 0 ? params : null;
            }),
        );
    }
}
